import { Injectable } from '@angular/core';
import { 
  PublicClientApplication, 
  AccountInfo, 
  Configuration,
  InteractionType,
  BrowserAuthError,
  InteractionRequiredAuthError,
  InteractionStatus,
  EventMessage,
  AuthenticationResult,
  EventType
} from '@azure/msal-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConfigService } from './app-config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _isLoggedIn = false;
  private msalInstance: PublicClientApplication | null = null;
  private account: AccountInfo | null = null;
  private msalConfig: Configuration | null = null;
  private redirectUrl: string | null = null;
  private interactionInProgress = false;
  private initializationPromise: Promise<boolean> | null = null;
  private loginAttemptCount = 0;
  private maxLoginAttempts = 3;
  private interactionStatus$ = new BehaviorSubject<string>('None');

  constructor(
    private router: Router,
    private appConfigService: AppConfigService
  ) {
    // Don't start initialization immediately - wait for config to be loaded
  }

  // This is the main initialization method that returns a promise
  private async initializeMsal(): Promise<boolean> {
    try {
      // Wait for config to be loaded first
      await this.appConfigService.loadConfig();
      
      // Get auth config from AppConfigService
      const authConfig = this.appConfigService.getAuthConfig();
      
      // Create MSAL config
      this.msalConfig = {
        auth: {
          clientId: authConfig.clientId,
          authority: authConfig.authority,
          redirectUri: authConfig.redirectUri,
          postLogoutRedirectUri: authConfig.postLogoutRedirectUri,
          navigateToLoginRequestUrl: true
        },
        cache: {
          cacheLocation: "sessionStorage",
          storeAuthStateInCookie: false
        },
        system: {
          allowRedirectInIframe: true,
          loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
              if (containsPii) return;
              if (level >= 3) {
                console.log(`MSAL: ${message}`);
              }
            },
            piiLoggingEnabled: false
          }
        }
      };
      
      // Log the configured URIs for debugging
      console.log('Auth config:', {
        redirectUri: authConfig.redirectUri,
        postLogoutRedirectUri: authConfig.postLogoutRedirectUri
      });
      
      // Create the MSAL instance
      this.msalInstance = new PublicClientApplication(this.msalConfig);
      
      // Set up event callbacks
      this.setupEventCallbacks();
      
      // Initialize MSAL explicitly
      await this.msalInstance.initialize();
      console.log('MSAL initialized successfully');
      
      // Handle any redirect response after initialization
      try {
        const response = await this.msalInstance.handleRedirectPromise();
        if (response) {
          console.log('Redirect response handled:', response.account?.username);
          this.account = response.account;
          this._isLoggedIn = true;
          this.msalInstance.setActiveAccount(this.account);
          
          // Reset login attempt counter on successful login
          this.loginAttemptCount = 0;
          this.interactionInProgress = false;
          this.interactionStatus$.next('None');
        }
      } catch (redirectError) {
        console.error('Error handling redirect:', redirectError);
        this.interactionInProgress = false;
        this.interactionStatus$.next('Error');
      }
      
      // Check for existing accounts
      this.checkAccounts();
      
      return true;
    } catch (error) {
      console.error('MSAL initialization failed:', error);
      this.interactionInProgress = false;
      this.interactionStatus$.next('Error');
      return false;
    }
  }

  private setupEventCallbacks(): void {
    if (!this.msalInstance) return;
    
    // Register event callbacks
    this.msalInstance.addEventCallback((event: EventMessage) => {
      // Update interaction status based on events
      if (event.eventType.includes('Start')) {
        this.interactionInProgress = true;
        this.interactionStatus$.next('InProgress');
      } else if (event.eventType.includes('Success') || event.eventType.includes('Failure') || event.eventType.includes('Cancel')) {
        this.interactionInProgress = false;
        this.interactionStatus$.next(event.eventType);
      }
      
      // Handle specific events
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const authResult = event.payload as AuthenticationResult;
        if (authResult.account) {
          this.account = authResult.account;
          this._isLoggedIn = true;
          this.msalInstance?.setActiveAccount(this.account);
        }
      }
    });
  }

  public async ensureMsalInitialized(): Promise<boolean> {
    // If we already have an initialization promise, wait for it
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    
    // If already initialized, return true
    if (this.msalInstance) {
      return true;
    }
    
    // Otherwise, start initialization
    this.initializationPromise = this.initializeMsal();
    return this.initializationPromise;
  }

  private checkAccounts(): void {
    if (!this.msalInstance) return;
    
    // If we already have an account, don't override it
    if (this.account) return;
    
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      // Try to find an account with the preferred domain
      const preferredDomain = 'aip.org';
      const domainAccounts = accounts.filter(acc => 
        acc.username.toLowerCase().endsWith('@' + preferredDomain.toLowerCase()));
      
      if (domainAccounts.length > 0) {
        this.account = domainAccounts[0];
      } else {
        this.account = accounts[0];
      }
      
      this._isLoggedIn = true;
      this.msalInstance.setActiveAccount(this.account);
    }
  }

  public isAuthenticated(): boolean {
    // Check if we're logged in according to our internal state
    if (this._isLoggedIn && this.account) {
      return true;
    }
    
    // Double-check with MSAL directly
    if (this.msalInstance) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.account = accounts[0];
        this._isLoggedIn = true;
        return true;
      }
    }
    
    return false;
  }

  async login(): Promise<void> {
    return this.loginRedirect();
  }

  /**
   * Initiates login using redirect flow
   * This is the preferred method for authentication, especially on mobile devices
   */
  async loginRedirect(): Promise<void> {
    // First, try to clear any existing interactions
    this.forceResetInteractionState();
    
    // Wait a moment to ensure state is reset
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      console.log('Starting new login redirect flow...');
      this.interactionInProgress = true;
      this.interactionStatus$.next('InProgress');
      this.loginAttemptCount++;
      
      // Ensure MSAL is initialized
      const initialized = await this.ensureMsalInitialized();
      if (!initialized || !this.msalInstance) {
        this.interactionInProgress = false;
        this.interactionStatus$.next('Error');
        throw new Error('Failed to initialize MSAL');
      }

      // Check if we're already logged in
      if (this.isAuthenticated()) {
        console.log('Already authenticated, navigating to dashboard');
        this.interactionInProgress = false;
        this.interactionStatus$.next('None');
        this.router.navigate(['/dashboard']);
        return;
      }

      // Use a more aggressive login request
      const loginRequest = {
        scopes: ['openid', 'profile', 'email', `api://${this.msalConfig?.auth.clientId}/access_as_user`],
        prompt: 'select_account', // Force account selection
        //redirectStartPage: window.location.origin + '/auth/login-callback' // Explicitly set redirect
      };

      console.log('Redirecting to Microsoft login page...');
      
      // Try to handle any pending redirects first
      try {
        await this.msalInstance.handleRedirectPromise();
      } catch (e) {
        console.log('Error handling redirect before login:', e);
      }
      
      // Now start the login redirect
      await this.msalInstance.loginRedirect(loginRequest);
      // Code after this won't execute due to redirect
    } catch (error) {
      this.interactionInProgress = false;
      this.interactionStatus$.next('Error');
      
      // Handle specific errors
      if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
        console.warn('Interaction in progress error detected, clearing state...');
        this.clearMsalStorage();
        
        // Wait a moment and try again
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }
      
      console.error('Login redirect failed:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    return this.logoutRedirect();
  }

  /**
   * Logs out the user using redirect flow
   */
  async logoutRedirect(): Promise<void> {
    if (this.interactionInProgress) {
      console.warn('Logout interaction already in progress. Ignoring new request.');
      return;
    }
    
    try {
      this.interactionInProgress = true;
      this.interactionStatus$.next('InProgress');
      
      const initialized = await this.ensureMsalInitialized();
      if (!initialized || !this.msalInstance) {
        this.interactionInProgress = false;
        this.interactionStatus$.next('Error');
        throw new Error('MSAL instance not initialized');
      }
      
      // Check if there's already an interaction in progress
      // We'll rely on our own tracking instead of getInteractionStatus()
      if (this.interactionInProgress) {
        console.warn('MSAL interaction already in progress. Cannot logout now.');
        this.interactionInProgress = false;
        this.interactionStatus$.next('Waiting');
        return;
      }
      
      if (this.account) {
        const accountToLogout = this.account;
        this.account = null;
        this._isLoggedIn = false;
        
        await this.msalInstance.logoutRedirect({
          postLogoutRedirectUri: this.msalConfig?.auth.postLogoutRedirectUri,
          account: accountToLogout
        });
        // Code after this won't execute due to redirect
      } else {
        this.interactionInProgress = false;
        this.interactionStatus$.next('None');
        this.router.navigate(['/auth/login']);
      }
    } catch (error) {
      this.interactionInProgress = false;
      this.interactionStatus$.next('Error');
      
      if (error instanceof BrowserAuthError && error.errorCode === 'uninitialized_public_client_application') {
        console.error('MSAL not initialized properly for logout. Redirecting to login page...');
        this.router.navigate(['/auth/login']);
        return;
      }
      
      console.error('Logout redirect failed:', error);
      throw error;
    }
  }

  // Get the current interaction status as an Observable
  getInteractionStatus(): Observable<string> {
    return this.interactionStatus$.asObservable();
  }

  // Reset the interaction state
  resetInteractionState(): void {
    this.interactionInProgress = false;
    this.interactionStatus$.next('None');
    this.loginAttemptCount = 0;
  }

  async acquireToken(): Promise<string> {
    try {
      const initialized = await this.ensureMsalInitialized();
      if (!initialized || !this.msalInstance) {
        throw new Error('Failed to initialize MSAL');
      }
      
      if (!this.account) {
        throw new Error('No active account');
      }
      
      const tokenRequest = {
        scopes: ['api://' + this.msalConfig?.auth.clientId + '/access_as_user'],
        account: this.account
      };

      try {
        const response = await this.msalInstance.acquireTokenSilent(tokenRequest);
        return response.accessToken;
      } catch (silentError) {
        if (this.interactionInProgress) {
          throw new Error('Authentication interaction already in progress');
        }
        
        console.warn('Silent token acquisition failed, using redirect', silentError);
        
        if (silentError instanceof InteractionRequiredAuthError) {
          this.setRedirectUrl(window.location.pathname);
          this.interactionInProgress = true;
          this.interactionStatus$.next('InProgress');
          this.msalInstance.acquireTokenRedirect(tokenRequest);
          throw new Error('Redirecting for interactive token acquisition');
        }
        
        throw silentError;
      }
    } catch (error) {
      if (error instanceof BrowserAuthError && error.errorCode === 'uninitialized_public_client_application') {
        console.error('MSAL not initialized properly for token acquisition. Reinitializing...');
        // Force reinitialization and try again
        this.initializationPromise = null;
        this.msalInstance = null;
        await this.ensureMsalInitialized();
        return this.acquireToken();
      }
      
      console.error('Token acquisition failed:', error);
      throw error;
    }
  }

  isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  getAccount(): AccountInfo | null {
    return this.account;
  }

  public autoSignIn(): void {
    if (!this.isLoggedIn()) {
      if (this.selectAccountByDomain('aip.org')) {
        this.router.navigate(['/dashboard']);
      } else {
        // Check if we can proceed with login
        if (!this.interactionInProgress && this.loginAttemptCount < this.maxLoginAttempts) {
          this.loginRedirect();
        } else {
          console.warn('Cannot auto sign in: interaction in progress or max attempts reached');
        }
      }
    }
  }

  // Select account by domain
  selectAccountByDomain(domain: string = 'aip.org'): boolean {
    if (!this.msalInstance) return false;
    
    const accounts = this.msalInstance.getAllAccounts();
    const domainAccounts = accounts.filter(acc => 
      acc.username.toLowerCase().endsWith('@' + domain.toLowerCase()));
    
    if (domainAccounts.length > 0) {
      this.account = domainAccounts[0];
      this._isLoggedIn = true;
      this.msalInstance.setActiveAccount(this.account);
      return true;
    }
    
    return false;
  }

  // Get all MSAL accounts
  getMsalAccounts(): AccountInfo[] {
    if (!this.msalInstance) return [];
    return this.msalInstance.getAllAccounts();
  }

  // Set active account
  setActiveAccount(account: AccountInfo): void {
    if (!this.msalInstance) return;
    
    this.msalInstance.setActiveAccount(account);
    this.account = account;
    this._isLoggedIn = true;
  }

  // Try to use stored account
  public tryUseStoredAccount(): boolean {
    if (!this.msalInstance) return false;
    
    // Check if there's a stored active account
    const activeAccount = this.msalInstance.getActiveAccount();
    
    if (activeAccount) {
      this.account = activeAccount;
      this._isLoggedIn = true;
      return true;
    }
    
    // If no active account, try domain-based selection
    return this.selectAccountByDomain('aip.org');
  }

  // Redirect URL management
  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string | null {
    return this.redirectUrl;
  }

  clearRedirectUrl(): void {
    this.redirectUrl = null;
  }
  
  // Check if an interaction is in progress
  isInteractionInProgress(): boolean {
    return this.interactionInProgress;
  }
  
  // We'll use our own tracking instead of getInteractionStatus()
  getNativeInteractionStatus(): string {
    return this.interactionStatus$.getValue();
  }

  isAuthReady(): boolean {
    // If we're authenticated or we've tried initialization, we're ready
    return this.isAuthenticated() || (this.initializationPromise !== null && !this.interactionInProgress);
  }

  // Update your forceResetInteractionState method
  public forceResetInteractionState(): void {
    console.log('Forcing reset of interaction state');
    
    // Reset our state
    this.interactionInProgress = false;
    this.interactionStatus$.next('None');
    this.loginAttemptCount = 0;
    
    try {
      // If we have MSAL instance, try to check its state
      if (this.msalInstance) {
        // Try to clear any pending redirects
        this.msalInstance.handleRedirectPromise().catch(e => {
          console.log('Error handling redirect during reset:', e);
        });
        
        // Check if we're actually authenticated despite the UI state
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          this.account = accounts[0];
          this._isLoggedIn = true;
          this.msalInstance.setActiveAccount(this.account);
          console.log('Found existing account during reset:', this.account.username);
        }
      }
    } catch (error) {
      console.error('Error checking accounts during reset:', error);
      
      // If we encounter an error, try to clear storage and reinitialize
      this.clearMsalStorage();
    }
  }

  // Add this method to your AuthService class
  public clearMsalStorage(): void {
    console.log('Clearing all MSAL storage');
    
    try {
      // Clear sessionStorage
      for (let key in sessionStorage) {
        if (key.startsWith('msal.') || key.includes('msal')) {
          console.log('Removing sessionStorage item:', key);
          sessionStorage.removeItem(key);
        }
      }
      
      // Clear localStorage
      for (let key in localStorage) {
        if (key.startsWith('msal.') || key.includes('msal')) {
          console.log('Removing localStorage item:', key);
          localStorage.removeItem(key);
        }
      }
      
      // Reset our state
      this.account = null;
      this._isLoggedIn = false;
      this.interactionInProgress = false;
      this.interactionStatus$.next('None');
      this.loginAttemptCount = 0;
      
      // Force MSAL reinitialization
      this.msalInstance = null;
      this.initializationPromise = null;
      
      console.log('MSAL storage cleared successfully');
    } catch (error) {
      console.error('Error clearing MSAL storage:', error);
    }
  }
}